import React, { useState, useEffect, useContext } from 'react';
import { FaPlus, FaUsers, FaTrash, FaSearch, FaFilter, FaDownload, FaCalendarAlt, FaTimes, FaUser } from 'react-icons/fa';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import Swal from 'sweetalert2';
import axios from 'axios';
import "react-datepicker/dist/react-datepicker.css";

const GuestMaster = () => {
  const { user } = useContext(AuthContext);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStation, setFilterStation] = useState('');
  const [filterSalesperson, setFilterSalesperson] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [stations, setStations] = useState([]);

    // Form state
  const [formData, setFormData] = useState({
    guestName: '',
    phoneNo: '',
    secondaryPhoneNo: '',
    email: '',
    station: null, // Changed to null for react-select
    reference: '',
    salesperson: null, // Changed to null for react-select
    registrationDate: new Date() // Added registration date with react-datepicker
  });

  const [formErrors, setFormErrors] = useState({});

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchGuests();
  }, [currentPage, searchTerm, filterStation, filterSalesperson]);
  
  // Separate effect to load station and salesperson data only once on component mount
  useEffect(() => {
    fetchSalespersons();
    fetchStations();
  }, []);

  const fetchGuests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        station: filterStation,
        salesperson: filterSalesperson,
        userCompanyId: user.user.company
      });

      const response = await fetch(`${API_BASE}/guests?${params}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setGuests(data.data);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalespersons = async () => {
    try {
      const companyId = user.user.company._id || user.user.company;
      const response = await axios.get(`${API_BASE}/employees/company2/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = response.data.employees;
      if (data) {
        // Format salespersons for react-select
        const salespersonOptions = data.map(person => ({
          value: person._id,
          label: `${person.name} (${person.id || ''})`
        }));
        setSalespersons(salespersonOptions);
      }
    } catch (error) {
      console.error('Error fetching salespersons:', error);
    }
  };

  const fetchStations = async () => {
    try {
      const response = await fetch(`${API_BASE}/activity-logs-new/stations/list`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        // Format stations for react-select
        const stationOptions = data.data.map(station => ({
          value: station._id,
          label: station.name
        }));
        setStations(stationOptions);
      } else if (Array.isArray(data)) {
        // Format stations for react-select
        const stationOptions = data.map(station => ({
          value: station._id,
          label: station.name
        }));
        setStations(stationOptions);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.guestName.trim()) {
      errors.guestName = 'Guest name is required';
    }
    
    if (!formData.phoneNo.trim()) {
      errors.phoneNo = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phoneNo.trim())) {
      errors.phoneNo = 'Phone number must be 10 digits';
    }

    // Secondary phone number validation (optional field)
    if (formData.secondaryPhoneNo && formData.secondaryPhoneNo.trim()) {
      if (!/^\d{10}$/.test(formData.secondaryPhoneNo.trim())) {
        errors.secondaryPhoneNo = 'Secondary phone number must be 10 digits';
      } else if (formData.secondaryPhoneNo.trim() === formData.phoneNo.trim()) {
        errors.secondaryPhoneNo = 'Secondary phone number must be different from primary phone number';
      }
    }

    // Email validation (optional field)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.email = 'Please enter a valid email address';
      }
    }
    
    if (!formData.station) {
      errors.station = 'Station is required';
    }
    
    if (!formData.reference.trim()) {
      errors.reference = 'Reference is required';
    }
    
    if (!formData.salesperson) {
      errors.salesperson = 'Salesperson is required';
    }
    
    if (!formData.registrationDate) {
      errors.registrationDate = 'Registration date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const payload = {
        guestName: formData.guestName,
        phoneNo: formData.phoneNo,
        secondaryPhoneNo: formData.secondaryPhoneNo,
        email: formData.email,
        station: formData.station.value, // Extract ObjectId from react-select value
        reference: formData.reference,
        salesperson: formData.salesperson.value, // Extract ObjectId from react-select value
        registrationDate: formData.registrationDate ? formData.registrationDate.toISOString() : null,
        createdBy: user.user._id
      };

      const response = await fetch(`${API_BASE}/guests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        await Swal.fire({
          title: 'Success!',
          text: 'Guest created successfully!',
          icon: 'success',
          confirmButtonColor: '#059669',
          timer: 2500,
          timerProgressBar: true
        });
        resetForm();
        fetchGuests();
      } else {
        // Handle validation errors
        if (data.errors && Array.isArray(data.errors)) {
          await Swal.fire({
            title: 'Validation Error',
            html: data.errors.map(error => `• ${error}`).join('<br>'),
            icon: 'error',
            confirmButtonColor: '#dc2626'
          });
        } else {
          await Swal.fire({
            title: 'Error!',
            text: data.message || 'Operation failed',
            icon: 'error',
            confirmButtonColor: '#dc2626'
          });
        }
      }
    } catch (error) {
      console.error('Error saving guest:', error);
      await Swal.fire({
        title: 'Error!',
        text: 'An error occurred while saving the guest',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const handleDelete = async (guestId) => {
    // Use SweetAlert2 for confirmation
    const result = await Swal.fire({
      title: 'Delete Guest?',
      text: 'Are you sure you want to delete this guest? This action cannot be undone and will also reset all guest IDs.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true); // Add loading state during delete
      
      // Show loading toast
      Swal.fire({
        title: 'Deleting guest...',
        text: 'Please wait while we delete the guest and reset IDs.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await fetch(`${API_BASE}/guests/${guestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        await Swal.fire({
          title: 'Success!',
          text: 'Guest deleted successfully and guest IDs have been reset.',
          icon: 'success',
          confirmButtonColor: '#059669',
          timer: 3000,
          timerProgressBar: true
        });
        fetchGuests(); // Refresh the list
      } else {
        await Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to delete guest',
          icon: 'error',
          confirmButtonColor: '#dc2626'
        });
      }
    } catch (error) {
      console.error('Error deleting guest:', error);
      await Swal.fire({
        title: 'Error!',
        text: 'An error occurred while deleting the guest',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      guestName: '',
      phoneNo: '',
      secondaryPhoneNo: '',
      email: '',
      station: null,
      reference: '',
      salesperson: null,
      registrationDate: new Date()
    });
    setFormErrors({});
    setShowCreateForm(false);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchGuests();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStation('');
    setFilterSalesperson('');
    setCurrentPage(1);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Guest Master</h1>
            <p className="text-gray-600 mt-1">Manage guest information and registrations</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors duration-200"
          >
            <FaPlus className="text-sm" />
            Add New Guest
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Search and Filter Section */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                  placeholder="Search by name, phone, email, or reference..."
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ textTransform: 'uppercase' }}
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Station</label>
              <Select
                value={stations?.find(option => option.value === filterStation)}
                onChange={(option) => setFilterStation(option ? option.value : '')}
                options={stations || []}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Filter by station..."
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    '&:hover': { border: '1px solid #d1d5db' },
                    '&:focus-within': { 
                      border: '2px solid #3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                    }
                  })
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salesperson</label>
              <Select
                value={salespersons?.find(option => option.value === filterSalesperson)}
                onChange={(option) => setFilterSalesperson(option ? option.value : '')}
                options={salespersons || []}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Filter by salesperson..."
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    '&:hover': { border: '1px solid #d1d5db' },
                    '&:focus-within': { 
                      border: '2px solid #3b82f6',
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
                    }
                  })
                }}
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <FaSearch /> Search
              </button>
              <button
                onClick={clearFilters}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <FaFilter /> Clear
              </button>
            </div>
          </div>
        </div>

        {/* Guest Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="flex justify-center items-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-gray-500">Loading guests...</span>
              </div>
            </div>
          ) : guests.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No guests found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your search or filter criteria.</p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <FaPlus className="mr-2" />
                  Add First Guest
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Guest Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Station
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Salesperson
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {guests.map((guest) => (
                    <tr key={guest._id} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {guest.guestName?.charAt(0)?.toUpperCase() || 'G'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{guest.guestName}</div>
                            <div className="text-sm text-gray-500">Guest ID: {guest._id?.slice(-6)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{guest.phoneNo}</div>
                        {guest.secondaryPhoneNo && (
                          <div className="text-sm text-gray-500">Secondary: {guest.secondaryPhoneNo}</div>
                        )}
                        {guest.email && (
                          <div className="text-sm text-gray-500">{guest.email}</div>
                        )}
                        <div className="text-sm text-gray-500">
                          Registered: {new Date(guest.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700">
                            {guest.station?.name || 'Not assigned'}
                          </div>
                          {guest.station?.state && (
                            <span className="ml-3 text-gray-600">{guest.station.state}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{guest.reference}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                              <span className="text-green-800 font-medium text-xs">
                                {guest.salesperson?.name?.charAt(0)?.toUpperCase() || 'S'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {guest.salesperson?.name || 'Not assigned'}
                            </div>
                            {guest.salesperson?.id && (
                              <div className="text-sm text-gray-500">ID: {guest.salesperson.id}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(guest.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => handleDelete(guest._id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md"
                            title="Delete Guest"
                          >
                            <FaTrash className="text-xs" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span> ({totalCount} total guests)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                    <FaUser className="text-white text-xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      Add New Guest
                    </h2>
                    <p className="text-blue-100 text-sm">Fill in the guest information below</p>
                  </div>
                </div>
                <button
                  onClick={resetForm}
                  className="text-white hover:text-blue-200 transition-colors duration-200 p-2 hover:bg-white hover:bg-opacity-20 rounded-lg"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Personal Information Section */}
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FaUser className="text-blue-600" />
                      Personal Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Guest Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.guestName}
                          onChange={(e) => setFormData({ ...formData, guestName: e.target.value.toUpperCase() })}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            formErrors.guestName ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Enter guest name"
                          style={{ textTransform: 'uppercase' }}
                        />
                        {formErrors.guestName && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.guestName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reference <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.reference}
                          onChange={(e) => setFormData({ ...formData, reference: e.target.value.toUpperCase() })}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            formErrors.reference ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Enter reference"
                          style={{ textTransform: 'uppercase' }}
                        />
                        {formErrors.reference && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.reference}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Registration Date <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <DatePicker
                            selected={formData.registrationDate}
                            onChange={(date) => setFormData({ ...formData, registrationDate: date })}
                            className={`w-full px-4 py-3 pl-12 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                              formErrors.registrationDate ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                            }`}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="Select registration date"
                          />
                          <FaCalendarAlt className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                        {formErrors.registrationDate && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.registrationDate}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Contact Information Section */}
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Contact Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Primary Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          value={formData.phoneNo}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 10) {
                              setFormData({ ...formData, phoneNo: value });
                            }
                          }}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            formErrors.phoneNo ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Enter 10-digit phone number"
                          maxLength={10}
                        />
                        {formErrors.phoneNo && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.phoneNo}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Secondary Phone Number 
                          <span className="text-gray-500 text-xs ml-1">(Optional)</span>
                        </label>
                        <input
                          type="tel"
                          value={formData.secondaryPhoneNo}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 10) {
                              setFormData({ ...formData, secondaryPhoneNo: value });
                            }
                          }}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            formErrors.secondaryPhoneNo ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Enter 10-digit secondary phone number"
                          maxLength={10}
                        />
                        {formErrors.secondaryPhoneNo && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.secondaryPhoneNo}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address 
                          <span className="text-gray-500 text-xs ml-1">(Optional)</span>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
                            formErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Enter email address"
                        />
                        {formErrors.email && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Assignment Information Section */}
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Assignment Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Station <span className="text-red-500">*</span>
                        </label>
                        <Select
                          value={formData.station}
                          onChange={(selectedOption) => setFormData({ ...formData, station: selectedOption })}
                          options={stations || []}
                          className={formErrors.station ? 'react-select-error' : ''}
                          classNamePrefix="react-select"
                          placeholder="Select Station"
                          isClearable
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: '48px',
                              border: formErrors.station 
                                ? '2px solid #ef4444' 
                                : state.isFocused 
                                  ? '2px solid #3b82f6' 
                                  : '2px solid #d1d5db',
                              borderRadius: '0.75rem',
                              backgroundColor: formErrors.station ? '#fef2f2' : 'white',
                              boxShadow: state.isFocused 
                                ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
                                : 'none',
                              '&:hover': {
                                borderColor: formErrors.station ? '#ef4444' : '#9ca3af'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected 
                                ? '#3b82f6' 
                                : state.isFocused 
                                  ? '#eff6ff' 
                                  : 'white',
                              color: state.isSelected ? 'white' : '#374151',
                              padding: '12px 16px'
                            })
                          }}
                        />
                        {formErrors.station && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.station}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Salesperson <span className="text-red-500">*</span>
                        </label>
                        <Select
                          value={formData.salesperson}
                          onChange={(selectedOption) => setFormData({ ...formData, salesperson: selectedOption })}
                          options={salespersons || []}
                          className={formErrors.salesperson ? 'react-select-error' : ''}
                          classNamePrefix="react-select"
                          placeholder="Select Salesperson"
                          isClearable
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: '48px',
                              border: formErrors.salesperson 
                                ? '2px solid #ef4444' 
                                : state.isFocused 
                                  ? '2px solid #3b82f6' 
                                  : '2px solid #d1d5db',
                              borderRadius: '0.75rem',
                              backgroundColor: formErrors.salesperson ? '#fef2f2' : 'white',
                              boxShadow: state.isFocused 
                                ? '0 0 0 3px rgba(59, 130, 246, 0.1)'
                                : 'none',
                              '&:hover': {
                                borderColor: formErrors.salesperson ? '#ef4444' : '#9ca3af'
                              }
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected 
                                ? '#3b82f6' 
                                : state.isFocused 
                                  ? '#eff6ff' 
                                  : 'white',
                              color: state.isSelected ? 'white' : '#374151',
                              padding: '12px 16px'
                            })
                          }}
                        />
                        {formErrors.salesperson && (
                          <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                            <span className="text-red-500">⚠</span>
                            {formErrors.salesperson}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium flex items-center justify-center gap-2 border border-gray-300"
                >
                  <FaTimes className="text-sm" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  <FaPlus className="text-sm" />
                  Create Guest
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestMaster;
